/**
 * The task screens' model (147, contract v3.1). The Task object and its words are 147's own, in api/tasks.ts (Code 1),
 * and are re-exported from here unchanged; what is added here is only what the screens need around them: the groups,
 * the events, the queues, the departments, the writes' inputs and a row's marks. Pure, so the tests and any component
 * can use it without React or a session. The hooks are api/taskQueries.ts.
 */
import type { Department, Person, Severity, Task, TaskObject, TaskStatus } from '../api/tasks.ts';
import type { DueSpec } from '../dueDates.ts';
export type { ClientTaskRow, Department, Person, RecordKind, Severity, Task, TaskObject, TaskSource, TaskStatus } from '../api/tasks.ts';
export type { DueSpec } from '../dueDates.ts';

export const DEPARTMENTS: Department[] = ['Social Work', 'Finance', 'Legal', 'Property', 'Compliance'];
export const SEVERITIES: Severity[] = ['Critical', 'High', 'Normal', 'Low'];

/**
 * Where a task came from, said in words where it is a death's (F-29, A-#375): a task 159 made when the client's death
 * was recorded. The row's tag and the card's line both read these, so the two cannot drift. No date: that waits on an
 * API change. Every other source says nothing here.
 */
export const DEATH_TASK_TAG = 'Death';
export const DEATH_TASK_WORDS = "Made when the client's death was recorded";
export const isDeathTask = (t: Pick<Task, 'source'>) => t.source === 'Death';

/** Emergency = Critical, ASAP = High (ruling 12); the others have no word of their own. */
export function severityWord(s: Severity): string | null {
  return s === 'Critical' ? 'Emergency' : s === 'High' ? 'ASAP' : null;
}

/**
 * 155: the Priority line under More. The shown priority (the badge's); when it rose by itself, what it is set as
 * too, so a director deciding whether to set it sees both (A-#212: a task the system made shows ASAP by its date, and
 * a client never visited shows ASAP always; a person's choice always stands). Staff words (A-#219): "raised
 * automatically" covers both reasons, and "set as" never says "stored".
 */
export function priorityWords(t: Pick<Task, 'severity' | 'severity_stored' | 'priority_risen'>): string {
  const shown = severityWord(t.severity) ? `${severityWord(t.severity)} (${t.severity})` : t.severity;
  return t.priority_risen ? `${shown}, raised automatically; set as ${t.severity_stored}` : shown;
}



export const OPEN_STATUSES: TaskStatus[] = ['Open', 'In Progress', 'Blocked'];
export const isOpen = (t: Pick<Task, 'status'>) => OPEN_STATUSES.includes(t.status);

export type GroupKey =
  | 'overdue' | 'emergency' | 'asap' | 'due_today' | 'due_tomorrow' | 'this_week' | 'next_week' | 'later' | 'done_this_week';
export interface TaskGroup { key: GroupKey; label: string; tasks: Task[] }
/**
 * Contract v2 (Arthur, A-#78): weeks Monday to Sunday, New York time; an overdue Critical task is in "overdue" only,
 * and the screen marks it Emergency from its severity. The groups come made and in order: the screen never re-groups.
 */
export interface MyTasks { today: string; groups: TaskGroup[] }

export type EventKind =
  | 'created' | 'assigned' | 'reassigned' | 'seen' | 'started' | 'completed' | 'waived' | 'cancelled' | 'priority'
  // Contract v6 (A-#115): a closed task reopened; detail "Was Done" (or Waived, Cancelled) and any reason. An
  // "assigned" for the new holding follows it, and seen, started and the completion restart.
  | 'reopened'
  // 153: the completion corrected; detail "Completion edited (version n): <reason>".
  | 'edited';
export interface TaskEvent { kind: EventKind; at: string; by: Person | null; holder: Person | null; detail: string | null }

/** GET /tasks/departments (contract v3.1): the only departments each form may offer; empty means not at all. */
export interface TaskDepartments {
  action: Department[]; create: Department[]; follow_up: Department[]; default: Department | null;
  /** A-#83 A5 (v4): the departments whose overdue work the reader may see (owning director, ECR, tiers 1-2). */
  department_overdue: Department[];
  /** A-#145: of those, the reader's own department (one they hold a role in): the panel on My tasks and the landings. */
  own_overdue: Department[];
}

export interface Queue { department: Department; owner_role: string | null; open: number; may_open: boolean }


/** Minutes, or the text the shared parser reads: never both (the contract's TIME SPENT). */
export type TimeSpent = { minutes: number } | { time: string };

export interface FollowUp {
  title: string;
  detail?: string;
  assigned_to_user_id?: string;
  due: DueSpec;
  severity?: Severity;
  /** 151: the department the assignee is listed under (GET /tasks/:id/follow-up-assignees); the task's otherwise. */
  department?: Department;
  /** 151: what the follow-up is about; the task's own object, carried when the reader sees it. */
  object?: { kind: TaskObject['kind']; id: string };
}

/**
 * 151: who a follow-up of a task may go to, and in which department, as the server lists them: anyone assigned to the
 * client (assigned), a colleague in the department (colleague), the department's director (director), the reader (you).
 */
export interface FollowUpAssignee { id: string; name: string; department: Department; why: 'assigned' | 'colleague' | 'director' | 'you' }

/** A-#154: who a follow-up went to, as the maker sent it: a person, or the department's queue (name null). */
export interface SentTo { name: string | null; department: string }

/** 150: a follow-up made for someone the reader may not see (a reader who works only their own tasks): its id, and who it went to. */
export type FollowUpMade = Task | { id: string; shown: false; sent_to: SentTo };

/**
 * The estimate marker (Arthur, A-#86 B1; contract v5): the server sends the deadline's provisional flag ONLY to the
 * System Administrator and the CPO, and null to everyone else. The screen shows "estimate" when the flag is exactly
 * true, and never asks a role. No task's words say "provisional".
 */
export function showsEstimate(t: Pick<Task, 'provisional'>): boolean {
  return t.provisional === true;
}

/** What a row says about a task beyond its title: the priority's word, overdue, done directly, an estimate. */
export function rowMarks(t: Pick<Task, 'severity' | 'due_date' | 'status' | 'source' | 'provisional'>, today: string) {
  return {
    // A-#205 2: no priority mark on a closed task (Done, Waived, Cancelled); its priority stays under More
    word: isOpen(t) ? severityWord(t.severity) : null,
    overdue: t.due_date !== null && t.due_date < today && isOpen(t),
    doneDirectly: t.source === 'Action',
    estimate: showsEstimate(t),
  };
}

/**
 * A-#83 A1 (contract v4): how a task is completed is the SERVER's word, on the task: complete_by "task" (Complete), or
 * the record that completes its deadline, made on that record's own screen: "filing", "visit", "site_visit", and since
 * 154 "commission" and "oath_designation" (A-#161 b, c: no longer plain Complete, A-#86 B3), each INSTEAD of Complete
 * (the server sets may.complete false). No list of rule codes here.
 */
/**
 * What the record's own screen is asked to open, in the router's state (never the URL): a filing's form for its
 * deadline, or a property's Record a site visit form (A-#191).
 */
export interface RecordOpen {
  record?: 'site_visit'; file_deadline?: string;
  /** A-#194: open THAT record, not its form: a filing's row on Reports (a site visit is its page's #site-visit-<id>) */
  show_filing?: string;
  /** 154 (A-#203): a commission's or an oath's order, its row in the Legal section's Commission panel */
  show_order?: string;
  /** deploy 8 (A-#227): where the record was opened from, said on its place by OpenedRecord */
  opened_from?: 'timeline' | 'task';
}

/**
 * A-#191: each record opens ITS OWN form, never a whole section: a filing, that deadline's filing form on Reports; a
 * site visit, that property's card on Property with its Record a site visit form open; a welfare visit, the visit
 * form for the client (its own page already).
 */
export function recordTarget(t: Pick<Task, 'complete_by' | 'record'>): { label: string; to: string; state?: RecordOpen } | null {
  const r = t.record;
  if (t.complete_by === 'task' || !r) return null;
  const c = r.client_id;
  switch (r.kind) {
    case 'filing': return { label: 'Record the filing', to: `/clients/${c}/reports`,
                            ...(r.deadline_id ? { state: { file_deadline: r.deadline_id } } : {}) };
    case 'visit': return { label: 'Record the visit', to: `/clients/${c}/social-work/visit` };
    case 'site_visit': return r.property_id
      ? { label: 'Record the site visit', to: `/clients/${c}/property/${r.property_id}`, state: { record: 'site_visit' } }
      : { label: 'Record the site visit', to: `/clients/${c}/property` };
    // 154 (A-#161 b, c): recorded against the deadline's order, in the Legal section's court orders.
    case 'commission': return { label: 'Record the commission', to: `/clients/${c}/legal`,
                                ...(r.court_order_id ? { state: { show_order: r.court_order_id, opened_from: 'task' } } : {}) };
    case 'oath_designation': return { label: 'Record the oath and designation', to: `/clients/${c}/legal`,
                                      ...(r.court_order_id ? { state: { show_order: r.court_order_id, opened_from: 'task' } } : {}) };
    // 162 (P-35): recorded against the deadline's order, in the Legal section's bonds
    case 'bond': return { label: 'Record the bond', to: `/clients/${c}/legal`,
                          ...(r.court_order_id ? { state: { show_order: r.court_order_id, opened_from: 'task' } } : {}) };
    // 166 (P-33): marshalling is recorded bank by bank, on the client's Finance section
    case 'marshalling': return { label: 'Record the next step at the bank', to: `/clients/${c}/finance` };
    case 'statements': return { label: 'Record the statements', to: `/clients/${c}/finance` };
    case 'bank_refused': return { label: 'Record the bank’s next answer', to: `/clients/${c}/finance` };
    case 'further_marshalling': return { label: 'Record the next step under the order', to: `/clients/${c}/finance` };
    // 167 (P-24, Q-D): a blocker's task is done by the record that clears it, on the client's commission line
    case 'commission_blocker': return { label: 'Open the commission line', to: `/clients/${c}/legal` };
    default: return null;
  }
}
